import type { MatchState } from "./match-controller";
import type { WinType } from "./match-controller";

export type GameState =
  | { kind: "boot" }
  | { kind: "menu"; profile?: { coins: number; gems: number } }
  | { kind: "match"; match: MatchState }
  | {
      kind: "results";
      outcome: "win" | "lose" | "draw";
      winType: WinType;
      round?: number;
      score?: [number, number];
      wins?: number;
    };

function assertState(
  state: Readonly<GameState>,
  kind: GameState["kind"],
): void {
  if (state.kind !== kind) {
    throw new Error(`Expected ${kind} state, received ${state.kind}`);
  }
}

export class GameStateMachine {
  #wins = 0;
  #profile = { coins: 0, gems: 0 };
  #state: GameState = { kind: "boot" };

  get state(): Readonly<GameState> {
    return this.#state;
  }

  completeBoot(profile = { coins: 0, gems: 0 }): void {
    assertState(this.#state, "boot");
    this.#wins = 0;
    this.#profile = profile;
    this.#state = { kind: "menu", profile };
  }

  startMatch(matchState: MatchState): void {
    // The practice (tutorial) match hands over to the real one without a menu
    // in between, so a running match may be replaced.
    if (this.#state.kind !== "match") assertState(this.#state, "menu");
    this.#state = { kind: "match", match: matchState };
  }

  updateMatch(matchState: MatchState): void {
    assertState(this.#state, "match");
    this.#state = { kind: "match", match: matchState };
  }

  finishMatch(outcome: "win" | "lose" | "draw", winType: WinType): void {
    assertState(this.#state, "match");
    const m = this.#state.kind === "match" ? this.#state.match : null;
    if (outcome === "win") this.#wins++;
    const score: [number, number] = [...(m?.roundsWon ?? [0, 0])];
    if (m && m.player.hp !== m.opponent.hp)
      score[m.player.hp > m.opponent.hp ? 0 : 1]++;
    this.#state = {
      kind: "results",
      outcome,
      winType,
      round: 3,
      score,
      wins: this.#wins,
    };
  }

  awardCoins(amount: number): void {
    this.#profile.coins += Math.max(0, Math.floor(amount));
  }

  returnToMenu(): void {
    assertState(this.#state, "results");
    this.#state = { kind: "menu", profile: this.#profile };
  }
}
